import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, MessageSquare, Plus, Receipt } from "lucide-react";
import RecentLoginsWidget from "@/components/RecentLoginsWidget";
import PremiumPhoneButton from "@/components/PremiumPhoneButton";
import ContactPreferences from "@/components/ContactPreferences";
import VoiceAssistantOnboarding from "@/components/VoiceAssistantOnboarding";
import AccountSecurityCheckup from "@/components/AccountSecurityCheckup";
import PrivacyControls from "@/components/PrivacyControls";

const STATUS_LABEL = {
  brouillon: "Brouillon", publiee: "Publiée", en_attente: "En attente",
  acceptee: "Acceptée", refusee: "Refusée", en_cours: "En cours",
  terminee: "Terminée", annulee: "Annulée",
};
const STATUS_COLOR = {
  publiee: "bg-[#0B3A2A]/10 text-[#0B3A2A]", en_attente: "bg-[#C9A961]/15 text-[#8A6E3A]",
  acceptee: "bg-[#7A9B7E]/15 text-[#5C7D62]", en_cours: "bg-[#8FA394]/15 text-[#115943]",
  terminee: "bg-slate-100 text-slate-700", refusee: "bg-[#8B3A3A]/12 text-[#8B3A3A]",
  annulee: "bg-[#8B3A3A]/12 text-[#8B3A3A]", brouillon: "bg-slate-100 text-slate-600",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/missions").then(r => setMissions(r.data)).finally(() => setLoading(false));
    apiClient.get("/payments/me").then(r => setPayments(r.data)).catch(() => setPayments([]));
  }, []);

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <VoiceAssistantOnboarding />
      <Navbar variant="light" />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Espace personnel · Utilisateur</div>
            <h1 className="font-serif text-4xl text-[#0B3A2A] tracking-tight">Bienvenue, {user?.first_name}</h1>
          </div>
          <Button asChild className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold h-11" data-testid="new-mission-btn">
            <Link to="/missions/new"><Plus className="w-4 h-4 mr-2" />Nouvelle demande</Link>
          </Button>
        </div>

        {/* Assistant vocal CONFILEX — bandeau discret */}
        <Card className="p-5 mb-8 rounded-2xl border-slate-100 bg-gradient-to-r from-white to-[#FAF7F1]/70" data-testid="dashboard-voice-assistant-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-1">Service premium</p>
              <h3 className="font-serif italic text-lg text-[#0B3A2A]">Structurer votre dossier par téléphone</h3>
              <p className="text-xs text-slate-600 mt-1 max-w-md leading-relaxed">
                Notre assistant vocal vous rappelle, recueille les éléments essentiels et prépare une synthèse exploitable.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Link to="/mes-appels" className="text-xs text-[#0B3A2A] hover:text-[#C9A961] underline underline-offset-2" data-testid="dashboard-my-calls-link">
                Voir mes appels
              </Link>
              <PremiumPhoneButton variant="pill" label="Être rappelé" />
            </div>
          </div>
        </Card>

        {/* Préférences de contact */}
        <div className="mb-8">
          <ContactPreferences />
        </div>

        {/* Check-up sécurité du compte */}
        <div className="mb-8">
          <AccountSecurityCheckup />
        </div>

        {/* RGPD self-service : export + suppression */}
        <div className="mb-8">
          <PrivacyControls />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {[
            { l: "Total demandes", v: missions.length },
            { l: "En cours", v: missions.filter(m => ["en_attente","acceptee","en_cours"].includes(m.status)).length },
            { l: "Terminées", v: missions.filter(m => m.status === "terminee").length },
          ].map(s => (
            <Card key={s.l} className="p-6 rounded-2xl border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{s.l}</div>
              <div className="font-serif text-3xl text-[#0B3A2A]">{s.v}</div>
            </Card>
          ))}
        </div>

        <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Mes demandes d'accompagnement</h2>
        {loading ? <p className="text-slate-500">Chargement…</p> :
        missions.length === 0 ? (
          <Card className="p-10 text-center rounded-2xl border-slate-100">
            <FileText className="w-10 h-10 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">Vous n'avez pas encore soumis de demande.</p>
            <Button asChild className="rounded-full bg-[#0B3A2A] text-white hover:bg-[#0B3A2A]">
              <Link to="/missions/new">Soumettre ma première demande</Link>
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {missions.map(m => (
              <Card key={m.id} className="p-6 rounded-2xl border-slate-100 hover:border-[#C9A961]/40 transition-smooth" data-testid={`mission-row-${m.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-serif text-lg text-[#0B3A2A]">{m.title}</h3>
                      <Badge className={`rounded-full ${STATUS_COLOR[m.status] || "bg-slate-100"}`}>{STATUS_LABEL[m.status]}</Badge>
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{m.description}</p>
                    <div className="text-xs text-slate-400 mt-2">{new Date(m.created_at).toLocaleDateString("fr-FR")} · {m.category}</div>
                  </div>
                  <Button asChild variant="outline" size="sm" className="rounded-full">
                    <Link to={`/messages/${m.id}`}><MessageSquare className="w-4 h-4 mr-2" />Messagerie</Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {payments.length > 0 && (
          <>
            <h2 className="font-serif text-2xl text-[#0B3A2A] mt-12 mb-6">Historique des règlements</h2>
            <div className="space-y-2" data-testid="payment-history">
              {payments.map(p => (
                <Card key={p.session_id} className="p-4 rounded-xl border-slate-100">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <Receipt className="w-5 h-5 text-[#C9A961] flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-[#0B3A2A] font-medium truncate">
                          {p.kind === "subscription" ? `Référencement ${p.tier}` : "Consultation"}
                        </div>
                        <div className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-[#0B3A2A]">{p.amount_eur?.toFixed(2)} €</span>
                      <Badge className={`rounded-full text-xs ${
                        p.payment_status === "paid" ? "bg-[#7A9B7E]/15 text-[#5C7D62]" :
                        p.payment_status === "initiated" ? "bg-[#C9A961]/15 text-[#8A6E3A]" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {p.payment_status === "paid" ? "Réglé" : p.payment_status === "initiated" ? "En attente" : p.payment_status}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Audit de connexion — widget « Dernières connexions » */}
        <div className="mt-10">
          <RecentLoginsWidget limit={5} />
        </div>
      </div>
      <Footer />
    </div>
  );
}
